import React, { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import css from './SubscriptionListingForm.css';

const SubscriptionListingForm = props => {
  const { onSubmit, initialValues, productCatalog, providers, isSubmitting, intl, isEdit, catalogItemTitle } = props;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: initialValues || {
      state: 'draft',
      minDurationMonths: 1,
    },
  });

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  const selectedCatalogProductId = useWatch({
    control,
    name: 'catalogProductId',
  });


  const availableItems = useMemo(() => {
    const selectedProduct = productCatalog?.find(p => p.id.uuid === selectedCatalogProductId);
    return selectedProduct?.catalogItems || [];
  }, [selectedCatalogProductId, productCatalog]);

  const currency = process.env.REACT_APP_COUNTRY_ID === 'pl' ? 'PLN' : 'CZK';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={css.root}>
      <div className={css.field}>
        <label className={css.label}>
          <FormattedMessage id="SubscriptionListingForm.providerLabel" />
        </label>
        {isEdit ? (
          <div className={css.readOnlyField}>
            <span className={css.readOnlyValue}>
              {providers.find(p => p.id.uuid === initialValues.providerId)?.attributes.name || 'Nieznany dostawca'}
            </span>
            {/* Ukryty input, aby react-hook-form wysłał ID przy zapisie */}
            <input type="hidden" {...register('providerId')} />
          </div>
        ) : (
          /* TRYB NOWY: Normalny select */
          <select
            {...register('providerId', { required: true })}
            className={errors.providerId ? css.inputError : css.input}
          >
            <option value="">{intl.formatMessage({ id: 'SubscriptionListingForm.selectProvider' })}</option>
            {providers && providers.map(provider => (
              <option key={provider.id.uuid} value={provider.id.uuid}>
                {provider.attributes.name}
              </option>
            ))}
          </select>
        )}

        {errors.providerId && <span className={css.errorText}>To pole jest wymagane</span>}
      </div>

      <div className={css.field}>
        <label className={css.label}>
          <FormattedMessage id="SubscriptionListingForm.productLabel" />
        </label>
        {isEdit ? (
          <div className={css.readOnlyField}>
            {/* Przy edycji zazwyczaj interesuje nas już tylko konkretny Item, 
                ale możemy tu wyświetlić nazwę modelu przekazaną z góry */}
            <span className={css.readOnlyValue}>{catalogItemTitle}</span>
            <input type="hidden" {...register('catalogProductId')} />
          </div>
        ) : (
          <select
            {...register('catalogProductId', { required: true })}
            className={errors.catalogProductId ? css.inputError : css.input}
          >
            <option value="">{intl.formatMessage({ id: 'SubscriptionListingForm.selectProduct' })}</option>
            {productCatalog?.map(product => (
              <option key={product.id.uuid} value={product.id.uuid}>
                {product.attributes.title}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* 3. WYBÓR KONKRETNEGO EGZEMPLARZA (ITEM) */}
      <div className={css.field}>
        <label className={css.label}>
          <FormattedMessage id="SubscriptionListingForm.itemLabel" />
        </label>
        {isEdit ? (
          <div className={css.readOnlyField}>
            <span className={css.readOnlyValue}>{initialValues?.catalogItemId}</span>
            <input type="hidden" {...register('catalogItemId')} />
          </div>
        ) : (
          <select
            {...register('catalogItemId', { required: true })}
            className={errors.catalogItemId ? css.inputError : css.input}
            disabled={!selectedCatalogProductId} // Blokada, dopóki model nie jest wybrany
          >
            <option value="">
              {!selectedCatalogProductId
                ? "Najpierw wybierz produkt..."
                : "Wybierz egzemplarz..."}
            </option>
            {availableItems.map(item => (
              <option key={item.id.uuid} value={item.id.uuid}>
                {item.attributes.title}
              </option>
            ))}
          </select>
        )}
        {errors.catalogItemId && <span className={css.errorText}>Wybór egzemplarza jest wymagany</span>}
      </div>

      <div className={css.row}>
        <div className={css.field}>
          <label className={css.label}>
            <FormattedMessage id="SubscriptionListingForm.priceLabel" />
          </label>
          <div className={css.inputWrapper}>
            <input
              type="number"
              {...register('price', { required: true, min: 1 })}
              placeholder="1000"
              className={errors.price ? css.inputError : css.input}
            />
            <span className={css.currencyPrefix}>{currency}</span>
          </div>
        </div>
        <div className={css.field}>
          <label className={css.label}>
            <FormattedMessage id="SubscriptionListingForm.stateLabel" />
          </label>
          <select {...register('state')} className={css.input}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      <div className={css.row}>
        {/* MINIMALNY OKRES */}
        <div className={css.field}>
          <label className={css.label}>
            <FormattedMessage id="SubscriptionListingForm.minDurationLabel" />
          </label>
          <input
            type="number"
            {...register('minDurationMonths', { required: true, min: 1 })}
            className={errors.minDurationMonths ? css.inputError : css.input}
          />
        </div>

        {/* ILOŚĆ SZTUK (Inventory) */}
        <div className={css.field}>
          <label className={css.label}>
            <FormattedMessage id="SubscriptionListingForm.amountLabel" />
          </label>
          <input
            type="number"
            {...register('managedAmount', { required: true, min: 0 })}
            className={errors.managedAmount ? css.inputError : css.input}
          />
        </div>
      </div>

      <button type="submit" className={css.submitButton} disabled={isSubmitting}>
        {isSubmitting ? (
          <FormattedMessage id="SubscriptionListingForm.saving" />
        ) : (
          <FormattedMessage id="SubscriptionListingForm.submit" />
        )}
      </button>
    </form>
  );
};

export default injectIntl(SubscriptionListingForm);